// MD3-inspired color tokens for EverCharge
// Primary: Green (#1B7340) — EV/charging brand identity

const textLight = {
  primary: 'rgba(26, 28, 25, 1)',
  secondary: 'rgba(26, 28, 25, 0.6)',
  disabled: 'rgba(26, 28, 25, 0.38)',
  hint: 'rgba(26, 28, 25, 0.38)'
};

const textDark = {
  primary: '#E2E3DE',
  secondary: 'rgba(226, 227, 222, 0.7)',
  disabled: 'rgba(226, 227, 222, 0.38)',
  hint: 'rgba(226, 227, 222, 0.38)'
};

const errorColor = {
  light: '#FFDAD6',
  main: '#BA1A1A',
  dark: '#93000A',
  contrastText: '#FFFFFF'
};

const md3GreenLight = {
  palette: {
    mode: 'light',
    primary: {
      light: '#54A870',
      main: '#1B7340',
      dark: '#00531F',
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: '#85C394',
      main: '#4E9B63',
      dark: '#2A7040',
      contrastText: '#FFFFFF'
    },
    error: errorColor,
    warning: {
      light: '#FFE0B2',
      main: '#E65100',
      dark: '#BF360C',
      contrastText: '#FFFFFF'
    },
    info: {
      light: '#B3E5FC',
      main: '#0277BD',
      dark: '#01579B',
      contrastText: '#FFFFFF'
    },
    success: {
      light: '#C8E6C9',
      main: '#2D9764',
      dark: '#1B5E20',
      contrastText: '#FFFFFF'
    },
    background: {
      paper: '#FFFFFF',
      default: '#F4F9F5'
    },
    text: textLight,
    divider: 'rgba(26, 28, 25, 0.12)'
  }
};

const md3GreenDark = {
  palette: {
    mode: 'dark',
    primary: {
      light: '#9BDFB1',
      main: '#6EC48A',
      dark: '#4DAB70',
      contrastText: '#003919'
    },
    secondary: {
      light: '#85C394',
      main: '#6EC48A',
      dark: '#4DAB70',
      contrastText: '#003919'
    },
    error: { main: '#FFB4AB', contrastText: '#690005' },
    warning: { main: '#FFB74D', contrastText: '#7F3700' },
    background: {
      paper: '#1C211D',
      default: '#131712'
    },
    text: textDark,
    divider: 'rgba(226, 227, 222, 0.12)'
  }
};

const legacySecondary = { light: '#85C394', main: '#4E9B63', dark: '#2A7040', contrastText: '#fff' };

export const themeColors = {
  md3Green: md3GreenLight,
  md3GreenDark: md3GreenDark,
  whitePurple: {
    palette: {
      mode: 'light',
      primary: { main: '#ffffff', contrastText: textLight.primary },
      secondary: { main: '#7467ef', contrastText: '#ffffff' },
      background: { paper: '#fff', default: '#f5f5f5' },
      error: errorColor,
      text: textLight
    }
  },
  whiteBlue: {
    palette: {
      mode: 'light',
      primary: { main: '#ffffff', contrastText: textLight.primary },
      secondary: { main: '#1976d2', contrastText: '#ffffff' },
      background: { paper: '#fff', default: '#f5f5f5' },
      error: errorColor,
      text: textLight
    }
  },
  slateDark1: {
    palette: {
      mode: 'dark',
      primary: { main: '#1C2333', contrastText: '#ffffff' },
      secondary: { main: '#6EC48A', contrastText: '#003919' },
      error: errorColor,
      background: { paper: '#1C2333', default: '#131922' },
      text: textDark
    }
  },
  slateDark2: {
    palette: {
      mode: 'dark',
      primary: { main: '#131922', contrastText: '#ffffff' },
      secondary: { main: '#6EC48A', contrastText: '#003919' },
      error: errorColor,
      background: { paper: '#1C2333', default: '#131922' },
      text: textDark
    }
  },
  purple1: {
    palette: {
      mode: 'light',
      primary: { main: '#7467ef', contrastText: '#ffffff' },
      secondary: legacySecondary,
      error: errorColor,
      background: { paper: '#fff', default: '#f5f5f5' },
      text: textLight
    }
  },
  purple2: {
    palette: {
      mode: 'light',
      primary: { main: '#6a75c9', contrastText: '#ffffff' },
      secondary: legacySecondary,
      error: errorColor,
      background: { paper: '#fff', default: '#f5f5f5' },
      text: textLight
    }
  },
  purpleDark1: {
    palette: {
      mode: 'dark',
      primary: { main: '#7467ef', contrastText: '#ffffff' },
      secondary: legacySecondary,
      error: errorColor,
      background: { paper: '#1C2333', default: '#131922' },
      text: textDark
    }
  },
  purpleDark2: {
    palette: {
      mode: 'dark',
      primary: { main: '#6a75c9', contrastText: '#ffffff' },
      secondary: legacySecondary,
      error: errorColor,
      background: { paper: '#1C2333', default: '#131922' },
      text: textDark
    }
  },
  blue: {
    palette: {
      mode: 'light',
      primary: { main: '#1976d2', contrastText: '#ffffff' },
      secondary: { main: '#FFAF38', contrastText: textLight.primary },
      error: errorColor,
      background: { paper: '#fff', default: '#f5f5f5' },
      text: textLight
    }
  },
  blueDark: {
    palette: {
      mode: 'dark',
      primary: { main: '#1976d2', contrastText: '#ffffff' },
      secondary: { main: '#FF4F30', contrastText: textLight.primary },
      error: errorColor,
      background: { paper: '#1C2333', default: '#131922' },
      text: textDark
    }
  },
  red: {
    palette: {
      mode: 'dark',
      primary: { main: '#e53935', contrastText: '#ffffff' },
      secondary: { main: '#FFAF38', contrastText: textLight.primary },
      error: errorColor,
      background: { paper: '#1C2333', default: '#131922' },
      text: textDark
    }
  }
};

// MD3-inspired shadows — subtle, tonal
export const themeShadows = [
  'none',
  '0px 1px 2px rgba(0,0,0,0.08)',
  '0px 1px 3px rgba(0,0,0,0.10), 0px 1px 2px rgba(0,0,0,0.06)',
  '0px 2px 4px rgba(0,0,0,0.08), 0px 1px 3px rgba(0,0,0,0.06)',
  '0px 4px 6px rgba(0,0,0,0.07), 0px 2px 4px rgba(0,0,0,0.06)',
  '0px 5px 8px rgba(0,0,0,0.07), 0px 3px 6px rgba(0,0,0,0.05)',
  '0px 6px 10px rgba(0,0,0,0.07), 0px 3px 6px rgba(0,0,0,0.05)',
  '0px 7px 12px rgba(0,0,0,0.07), 0px 4px 8px rgba(0,0,0,0.04)',
  '0px 8px 14px rgba(0,0,0,0.06), 0px 4px 8px rgba(0,0,0,0.04)',
  '0px 9px 16px rgba(0,0,0,0.06), 0px 5px 10px rgba(0,0,0,0.04)',
  '0px 10px 18px rgba(0,0,0,0.06), 0px 5px 10px rgba(0,0,0,0.04)',
  '0px 11px 20px rgba(0,0,0,0.05), 0px 6px 12px rgba(0,0,0,0.04)',
  '0px 12px 22px rgba(0,0,0,0.05), 0px 6px 12px rgba(0,0,0,0.04)',
  '0px 13px 24px rgba(0,0,0,0.05), 0px 7px 14px rgba(0,0,0,0.04)',
  '0px 14px 26px rgba(0,0,0,0.05), 0px 7px 14px rgba(0,0,0,0.03)',
  '0px 15px 28px rgba(0,0,0,0.05), 0px 8px 16px rgba(0,0,0,0.03)',
  '0px 16px 30px rgba(0,0,0,0.04), 0px 8px 16px rgba(0,0,0,0.03)',
  '0px 17px 32px rgba(0,0,0,0.04), 0px 9px 18px rgba(0,0,0,0.03)',
  '0px 18px 34px rgba(0,0,0,0.04), 0px 9px 18px rgba(0,0,0,0.03)',
  '0px 19px 36px rgba(0,0,0,0.04), 0px 10px 20px rgba(0,0,0,0.03)',
  '0px 20px 38px rgba(0,0,0,0.03), 0px 10px 20px rgba(0,0,0,0.02)',
  '0px 20px 38px rgba(0,0,0,0.03), 0px 10px 20px rgba(0,0,0,0.02)',
  '0px 20px 38px rgba(0,0,0,0.03), 0px 10px 20px rgba(0,0,0,0.02)',
  '0px 20px 38px rgba(0,0,0,0.03), 0px 10px 20px rgba(0,0,0,0.02)',
  '0px 20px 38px rgba(0,0,0,0.03), 0px 10px 20px rgba(0,0,0,0.02)'
];
